import type { FieldProps } from '@rjsf/utils';
import classNames from 'classnames';
import type { CheckboxProps } from 'semantic-ui-react';
import { Checkbox, Divider } from 'semantic-ui-react';

/**
 * Custom field component for banner sections (top/bottom) with enable/disable
 * checkbox.
 */
export const BannerSectionField = (props: FieldProps) => {
  const { uiSchema, formData, onChange, disabled, readonly, name, registry } =
    props;

  // Determine whether to show divider from UI schema options
  const showDivider = uiSchema?.['ui:options']?.showDivider === true;

  // Derive enabled state directly from `formData`:
  // - Consider it enabled if formData is an object (even if empty)
  // - `undefined` or `null` means disabled (unchecked)
  // - `{}` (empty object) means enabled but no values yet (checked)
  // - An object with properties means enabled with values (checked)
  const isEnabled =
    formData !== null && formData !== undefined && typeof formData === 'object';

  // Handle the checkbox toggle
  const handleToggle = (e: React.FormEvent, checkboxData: CheckboxProps) => {
    // Use the checkbox's own checked state rather than our derived `isEnabled`
    // as this ensures we respond to the actual click event
    const willBeEnabled = checkboxData.checked ?? false;

    if (willBeEnabled) {
      // Enabling editing
      onChange({});
    } else {
      // Disabling editing
      onChange(null);
    }
  };

  // Get the `ObjectField` from registry to render the object's fields
  const { fields } = registry;
  const { ObjectField } = fields;

  // Format the label for the checkbox
  const label = name ? name.charAt(0).toUpperCase() + name.slice(1) : 'Enable';

  return (
    <div className="rjsf-banner-section-field">
      {/* Section header with checkbox */}
      <div className="field rjsf-banner-section-header">
        <div className="rjsf-banner-section-toggle">
          <Checkbox
            checked={isEnabled}
            onChange={handleToggle}
            disabled={disabled || readonly}
            id={`banner-checkbox-${name}`}
          />
          <label
            htmlFor={`banner-checkbox-${name}`}
            className={classNames('rjsf-banner-section-label', {
              disabled: disabled || readonly,
            })}
          >
            <h4 className="ui header rjsf-banner-section-heading">
              {label} Banner
            </h4>
          </label>
        </div>
      </div>

      {/* Show object fields only when enabled */}
      {isEnabled && (
        <div className="rjsf-banner-section-content">
          <ObjectField
            {...props}
            formData={formData || {}}
            onChange={onChange}
          />
        </div>
      )}
      {/* Show divider based on UI schema configuration */}
      {showDivider && <Divider />}
    </div>
  );
};
